import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, Calendar, ArrowRight, ArrowUpDown, Hash } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '../ui/button';
import { useTheme } from '../../context/ThemeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Custom marker icon
const customIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

export const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [blogCategories, setBlogCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeYear, setActiveYear] = useState('all');
  const [sortAsc, setSortAsc] = useState(false);
  const [loading, setLoading] = useState(true);
  const { theme } = useTheme();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [postsRes, catsRes] = await Promise.all([
          fetch(`${API}/blog`),
          fetch(`${API}/blog-categories`)
        ]);
        const postsData = await postsRes.json();
        const catsData = await catsRes.json();
        setPosts(postsData);
        setBlogCategories(Array.isArray(catsData) ? catsData : []);
      } catch (error) {
        console.error('Error fetching blog data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Use defined blog categories from admin
  const availableCategories = useMemo(() => {
    const catNames = blogCategories.map(c => c.name).filter(Boolean);
    return ['all', ...catNames];
  }, [blogCategories]);

  // Get available years from posts
  const availableYears = useMemo(() => {
    const years = posts
      .map(p => p.date ? new Date(p.date).getFullYear() : null)
      .filter(y => y !== null);
    return ['all', ...Array.from(new Set(years)).sort((a, b) => b - a)];
  }, [posts]);

  // Filter posts by category and year, then sort
  const filteredPosts = useMemo(() => {
    const filtered = posts.filter(p => {
      const matchesCategory = activeCategory === 'all' || p.category === activeCategory;
      const postYear = p.date ? new Date(p.date).getFullYear() : null;
      const matchesYear = activeYear === 'all' || postYear === activeYear;
      return matchesCategory && matchesYear;
    });
    return [...filtered].sort((a, b) => {
      const dateA = new Date(a.date || 0);
      const dateB = new Date(b.date || 0);
      return sortAsc ? dateA - dateB : dateB - dateA;
    });
  }, [posts, activeCategory, activeYear, sortAsc]);

  const postsWithCoords = useMemo(() => 
    filteredPosts.filter(p => p.coordinates && p.coordinates.length === 2),
    [filteredPosts]
  );

  const tileUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
  const mapClass = theme === 'dark' ? 'leaflet-tiles-dark' : '';

  return (
    <section id="blog" className="py-20 md:py-32 bg-background" data-testid="blog-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-sm uppercase tracking-widest text-muted-foreground block mb-4">
            Blog
          </span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight mb-6">
            Diario de un Fotógrafo
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Más allá de los trabajos por encargo
          </p>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="flex flex-col items-center gap-4 mb-12"
        >
          {/* Category filters */}
          <div className="flex flex-wrap justify-center gap-2">
            {availableCategories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 text-xs uppercase tracking-widest transition-colors ${
                  activeCategory === category
                    ? 'bg-foreground text-background'
                    : 'bg-transparent text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`blog-filter-${category.toLowerCase()}`}
              >
                {category === 'all' ? 'Todos' : category}
              </button>
            ))}
          </div>
          
          {/* Year filters */}
          {availableYears.length > 2 && (
            <div className="flex flex-wrap justify-center gap-2">
              {availableYears.map((year) => (
                <button
                  key={year}
                  onClick={() => setActiveYear(year)}
                  className={`px-3 py-1 text-xs transition-colors border ${
                    activeYear === year
                      ? 'border-foreground bg-foreground text-background'
                      : 'border-border text-muted-foreground hover:border-foreground hover:text-foreground'
                  }`}
                  data-testid={`blog-year-${year}`}
                >
                  {year === 'all' ? 'Todos los años' : year}
                </button>
              ))}
            </div>
          )}

          {/* Sort toggle */}
          <button
            onClick={() => setSortAsc(!sortAsc)}
            className="flex items-center gap-2 px-3 py-1 text-xs border border-border text-muted-foreground hover:border-foreground hover:text-foreground transition-colors"
            data-testid="blog-sort-toggle"
          >
            <ArrowUpDown className="w-3 h-3" />
            {sortAsc ? 'Más antiguo primero' : 'Más reciente primero'}
          </button>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Posts Grid */}
          <div>
            {loading ? (
              <div className="grid grid-cols-1 gap-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-32 bg-muted animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6">
                {filteredPosts.map((post, index) => (
                  <motion.div
                    key={post.post_id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <Link 
                      to={`/blog/${post.post_id}`}
                      className="group flex gap-4 p-4 bg-card border border-border hover:border-foreground/20 transition-colors"
                      data-testid={`blog-post-${post.post_id}`}
                    >
                      <div className="w-24 h-24 md:w-32 md:h-32 flex-shrink-0 overflow-hidden">
                        <img
                          src={post.thumbnail}
                          alt={post.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="flex-1 flex flex-col justify-center">
                        <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {post.date}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {post.location}
                          </span>
                        </div>
                        <h3 className="font-serif text-lg md:text-xl mb-2 group-hover:text-primary transition-colors">
                          {post.title}
                        </h3>
                        <p className="text-sm text-muted-foreground line-clamp-2">{post.excerpt}</p>
                        {post.tags && post.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {post.tags.map((tag, i) => (
                              <span key={i} className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-muted text-xs text-muted-foreground">
                                <Hash className="w-2.5 h-2.5" />{tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* Map */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="h-[400px] lg:h-full min-h-[400px] border border-border overflow-hidden"
          >
            <MapContainer
              center={[42.8, -2.5]}
              zoom={7}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={false}
              className={mapClass}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url={tileUrl}
              />
              {postsWithCoords.map((post) => (
                <Marker
                  key={post.post_id}
                  position={[post.coordinates[1], post.coordinates[0]]}
                  icon={customIcon}
                >
                  <Popup>
                    <Link to={`/blog/${post.post_id}`} className="block w-48 no-underline text-inherit">
                      {post.thumbnail && (
                        <img 
                          src={post.thumbnail} 
                          alt={post.title}
                          className="w-full h-24 object-cover mb-2 rounded"
                        />
                      )}
                      <h4 className="font-medium text-sm mb-1 text-foreground hover:underline">{post.title}</h4>
                      <p className="text-xs text-muted-foreground mb-2">{post.location}</p>
                      <span className="text-xs text-primary font-medium">Ver entrada →</span>
                    </Link>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </motion.div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <Link to="/blog">
            <Button 
              variant="outline" 
              className="rounded-none border-foreground text-foreground hover:bg-foreground hover:text-background transition-colors duration-300 px-8 py-3 uppercase tracking-widest text-xs"
              data-testid="blog-cta"
            >
              Ver Todo el Blog
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
